import type { TrelloCardFields } from './mapTrelloCardDataToCard';
import {
  validateCardMemberData,
  type MemberData,
} from './validateCardMemberData';
import { isBool, isNumber, isString, nullOrString } from './validateHelpers';
import {
  validateTrelloCardBadgesData,
  type Badges,
} from './validateTrelloCardBadgesData';
import {
  validateTrelloCardBoardData,
  type TrelloCardBoardFields,
} from './validateTrelloCardBoardData';
import {
  validateTrelloCardCoverData,
  type Cover,
} from './validateTrelloCardCoverData';
import {
  validateTrelloCardCustomFieldItemsData,
  type CustomFieldItems,
} from './validateTrelloCardCustomFieldItemsData';
import {
  validateTrelloCardPowerUpData,
  type TrelloCardPowerUpData,
} from './validateTrelloCardPowerUpData';
import { validateTrelloListData, type List } from './validateTrelloListData';
import {
  validateTrelloStickerData,
  type TrelloStickerFields,
} from './validateTrelloStickerData';

export const validateTrelloCardData = (inputCard: {
  objectId?: string;
  attachments?: {
    edges: {
      node: {
        objectId?: string | null;
        isMalicious?: boolean | null;
      };
    }[];
  } | null;
  badges?: Badges;
  board?: TrelloCardBoardFields;
  closed?: boolean | null;
  cover?: Cover | null;
  customFieldItems?: CustomFieldItems | null;
  complete?: boolean | null;
  due?: {
    at?: string | null;
  } | null;
  isTemplate?: boolean | null;
  labels?: {
    edges: {
      node: {
        color?: string | null;
        name?: string | null;
        objectId?: string | null;
      };
    }[];
  } | null;
  lastActivityAt?: string | null;
  limits?: {
    stickers?: {
      perCard?: {
        disableAt?: number | null;
      } | null;
    } | null;
  } | null;
  list?: List | null;
  mirrorSourceId?: string | null;
  name?: string | null;
  members?: MemberData | null;
  pinned?: boolean | null;
  powerUpData?: TrelloCardPowerUpData | null;
  shortLink?: string | null;
  stickers?: TrelloStickerFields;
  role?: string | null;
}): inputCard is TrelloCardFields => {
  const {
    objectId,
    attachments,
    board,
    badges,
    closed,
    cover,
    complete,
    customFieldItems,
    due,
    isTemplate,
    labels,
    lastActivityAt,
    limits,
    list,
    members,
    mirrorSourceId,
    name,
    pinned,
    powerUpData,
    shortLink,
    stickers,
    role,
  } = inputCard;

  return (
    isString(objectId) &&
    Array.isArray(attachments?.edges) &&
    attachments.edges.every((edge) => {
      const node = edge.node;
      return isString(node.objectId) && isBool(node.isMalicious);
    }) &&
    isBool(closed) &&
    isString(shortLink) &&
    validateTrelloCardBadgesData(badges) &&
    validateTrelloCardBoardData(board) &&
    ((!!cover && validateTrelloCardCoverData(cover)) || cover === null) &&
    validateTrelloCardCustomFieldItemsData(customFieldItems) &&
    isBool(complete) &&
    (nullOrString(due?.at) || due === null) &&
    isBool(isTemplate) &&
    Array.isArray(labels?.edges) &&
    labels.edges.every((edge) => {
      const node = edge.node;
      return (
        nullOrString(node.color) &&
        isString(node.name) &&
        isString(node.objectId)
      );
    }) &&
    isString(lastActivityAt) &&
    isNumber(limits?.stickers?.perCard?.disableAt) &&
    ((!!list && validateTrelloListData(list)) || list === null) &&
    nullOrString(mirrorSourceId) &&
    ((!!members && validateCardMemberData(members)) || members === null) &&
    !!powerUpData &&
    validateTrelloCardPowerUpData(powerUpData) &&
    nullOrString(name) &&
    isBool(pinned) &&
    nullOrString(role) &&
    ((!!stickers && validateTrelloStickerData(stickers)) || stickers === null)
  );
};
